import { Plus } from 'lucide-react';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { SheetBody } from '@/components/custom/sheet-layout';
import { TILE_DEFINITIONS, type TileDefinition } from './tile-registry';
import type { TileType } from '@vagusx/clients';

/**
 * Sliding panel listing every tile type the user can add. Clicking
 * "Add" inserts a new tile with the type's `defaultSize` at the
 * bottom of the layout — react-grid-layout's compaction does the
 * rest.
 *
 * Tiles already in the layout still show in the catalog (we don't
 * de-dupe — the user might want two of the same tile with different
 * configs in the future). The catalog row shows a count of how many
 * of this type are already on the dashboard so the user has the
 * signal without forcing a no-dupes rule.
 */
export function TileCatalogDrawer({
  onAdd,
  countByType,
  disabled,
}: {
  onAdd: (def: TileDefinition) => void;
  countByType: Record<string, number>;
  disabled?: boolean;
}): React.ReactElement {
  return (
    <Sheet>
      <SheetTrigger asChild>
        <Button variant="outline" size="sm" disabled={disabled}>
          <Plus className="mr-1 h-4 w-4" />
          Add tile
        </Button>
      </SheetTrigger>
      <SheetContent className="w-[400px] sm:w-[480px]">
        <SheetHeader>
          <SheetTitle>Tile catalog</SheetTitle>
          <SheetDescription>
            Pick a tile to add to your dashboard. You can resize and reposition it after it&apos;s
            placed.
          </SheetDescription>
        </SheetHeader>
        <SheetBody className="pb-6">
          <ul className="mt-4 flex flex-col gap-2">
            {TILE_DEFINITIONS.map((def) => {
              const Icon = def.icon;
              const count = countByType[def.type] ?? 0;
              return (
                <li
                  key={def.type}
                  className="flex items-start gap-3 rounded-lg border p-3 transition-colors hover:bg-muted/40"
                >
                  <Icon className="mt-0.5 h-5 w-5 shrink-0 text-muted-foreground" />
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between gap-2">
                      <div className="text-sm font-medium">{def.title}</div>
                      {count > 0 && (
                        <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
                          {count} on board
                        </span>
                      )}
                    </div>
                    <div className="mt-0.5 text-xs text-muted-foreground">{def.description}</div>
                  </div>
                  <Button size="sm" variant="ghost" onClick={() => onAdd(def)}>
                    Add
                  </Button>
                </li>
              );
            })}
          </ul>
        </SheetBody>
      </SheetContent>
    </Sheet>
  );
}

/**
 * Helper to derive `countByType` from the active layout — exported so
 * the page can compute it once and pass it in.
 */
export function tileCountsByType(layout: Array<{ type: string }>): Record<string, number> {
  return layout.reduce<Record<string, number>>((acc, t) => {
    acc[t.type] = (acc[t.type] ?? 0) + 1;
    return acc;
  }, {});
}

export type { TileType };
